import { type FC, useCallback, useState } from "react";
import type { MetaAdsGeoSearchResult } from "shared";
import { useDebounceValue } from "usehooks-ts";
import { useGeoSearch } from "../useGeoSearch";
import { useGeoTargeting } from "../useGeoTargeting";

export const ExclusionPanel: FC = () => {
	const [open, setOpen] = useState(false);
	const { exclusions } = useGeoTargeting();

	const totalExclusions =
		exclusions.zipCodes.length +
		exclusions.cities.length +
		exclusions.regions.length +
		exclusions.customLocations.length;

	return (
		<div style={{ border: "1px solid #dee2e6", borderRadius: 6, padding: 8 }}>
			<button
				type="button"
				className="btn btn-sm btn-link text-decoration-none p-0"
				onClick={() => setOpen(!open)}
				style={{ fontSize: 13, fontWeight: 500, color: "#dc3545" }}
			>
				{open ? "\u25BC" : "\u25B6"} Exclusion Zones
				{totalExclusions > 0 && (
					<span className="badge bg-danger ms-2">{totalExclusions}</span>
				)}
			</button>

			{open && (
				<div
					style={{
						marginTop: 8,
						display: "flex",
						flexDirection: "column",
						gap: 8,
					}}
				>
					<ExclusionZipSection />
					<ExclusionCitySection />
					<ExclusionRegionSection />
					<ExclusionPinSection />
				</div>
			)}
		</div>
	);
};

const ExclusionZipSection: FC = () => {
	const { exclusions, addExclusionZip, removeExclusionZip } = useGeoTargeting();
	const [query, setQuery] = useState("");
	const [debouncedQuery] = useDebounceValue(query, 400);
	const { data: results } = useGeoSearch(debouncedQuery, "zip");

	const handleSelect = useCallback(
		(r: MetaAdsGeoSearchResult) => {
			addExclusionZip({
				key: r.key,
				name: r.name,
				primaryCity: r.primary_city ?? "",
				region: r.region ?? "",
				country: r.country_code,
			});
			setQuery("");
		},
		[addExclusionZip],
	);

	return (
		<ExclusionSearchSection
			label="Exclude Zip Codes"
			placeholder="Search zip codes to exclude..."
			query={query}
			onQueryChange={setQuery}
			results={results?.filter((r) => r.type === "zip")}
			onSelect={handleSelect}
			items={exclusions.zipCodes}
			onRemove={removeExclusionZip}
			renderItem={(z) => z.name}
		/>
	);
};

const ExclusionCitySection: FC = () => {
	const { exclusions, addExclusionCity, removeExclusionCity } =
		useGeoTargeting();
	const [query, setQuery] = useState("");
	const [debouncedQuery] = useDebounceValue(query, 400);
	const { data: results } = useGeoSearch(debouncedQuery, "city");

	const handleSelect = useCallback(
		(r: MetaAdsGeoSearchResult) => {
			addExclusionCity({
				key: r.key,
				name: r.name,
				region: r.region ?? "",
				country: r.country_code,
			});
			setQuery("");
		},
		[addExclusionCity],
	);

	return (
		<ExclusionSearchSection
			label="Exclude Cities"
			placeholder="Search cities to exclude..."
			query={query}
			onQueryChange={setQuery}
			results={results?.filter((r) => r.type === "city")}
			onSelect={handleSelect}
			items={exclusions.cities}
			onRemove={removeExclusionCity}
			renderItem={(c) => `${c.name}${c.region ? `, ${c.region}` : ""}`}
		/>
	);
};

const ExclusionRegionSection: FC = () => {
	const { exclusions, addExclusionRegion, removeExclusionRegion } =
		useGeoTargeting();
	const [query, setQuery] = useState("");
	const [debouncedQuery] = useDebounceValue(query, 400);
	const { data: results } = useGeoSearch(debouncedQuery, "region");

	const handleSelect = useCallback(
		(r: MetaAdsGeoSearchResult) => {
			addExclusionRegion({
				key: r.key,
				name: r.name,
				country: r.country_code,
			});
			setQuery("");
		},
		[addExclusionRegion],
	);

	return (
		<ExclusionSearchSection
			label="Exclude Regions"
			placeholder="Search regions to exclude..."
			query={query}
			onQueryChange={setQuery}
			results={results?.filter((r) => r.type === "region")}
			onSelect={handleSelect}
			items={exclusions.regions}
			onRemove={removeExclusionRegion}
			renderItem={(r) => `${r.name} (${r.country})`}
		/>
	);
};

const ExclusionPinSection: FC = () => {
	const { exclusions, removeExclusionPin } = useGeoTargeting();

	return (
		<div>
			<span
				className="form-label"
				style={{ fontSize: 12, fontWeight: 500, color: "#dc3545" }}
			>
				Exclude Radius Pins
			</span>
			{exclusions.customLocations.length > 0 && (
				<div
					style={{
						display: "flex",
						flexWrap: "wrap",
						gap: 4,
						marginBottom: 4,
					}}
				>
					{exclusions.customLocations.map((pin) => (
						<button
							type="button"
							key={pin.id}
							className="badge bg-danger border-0"
							style={{
								display: "inline-flex",
								alignItems: "center",
								gap: 4,
								cursor: "pointer",
								fontSize: 12,
							}}
							onClick={() => removeExclusionPin(pin.id)}
						>
							{pin.label} ({pin.radius}{" "}
							{pin.distanceUnit === "mile" ? "mi" : "km"})
							<span style={{ fontWeight: "bold" }}>&times;</span>
						</button>
					))}
				</div>
			)}
			<div className="text-muted" style={{ fontSize: 11 }}>
				Click on the map to add exclusion radius pins (shown in red).
			</div>
		</div>
	);
};

interface ExclusionSearchSectionProps<T extends { key: string }> {
	label: string;
	placeholder: string;
	query: string;
	onQueryChange: (q: string) => void;
	results: MetaAdsGeoSearchResult[] | undefined;
	onSelect: (r: MetaAdsGeoSearchResult) => void;
	items: T[];
	onRemove: (key: string) => void;
	renderItem: (item: T) => string;
}

function ExclusionSearchSection<T extends { key: string }>({
	label,
	placeholder,
	query,
	onQueryChange,
	results,
	onSelect,
	items,
	onRemove,
	renderItem,
}: ExclusionSearchSectionProps<T>) {
	const inputId = `exclusion-${label.toLowerCase().replace(/\s+/g, "-")}`;
	return (
		<div>
			<label
				htmlFor={inputId}
				className="form-label"
				style={{ fontSize: 12, fontWeight: 500, color: "#dc3545" }}
			>
				{label}
			</label>
			<input
				id={inputId}
				type="text"
				className="form-control form-control-sm"
				placeholder={placeholder}
				value={query}
				onChange={(e) => onQueryChange(e.target.value)}
				style={{ fontSize: 12 }}
			/>
			{query.length >= 2 && results && results.length > 0 && (
				<div
					className="list-group mt-1"
					style={{ maxHeight: 100, overflowY: "auto" }}
				>
					{results.map((r) => (
						<button
							key={r.key}
							type="button"
							className="list-group-item list-group-item-action py-1 px-2"
							style={{ fontSize: 12 }}
							onClick={() => onSelect(r)}
						>
							{r.name}
						</button>
					))}
				</div>
			)}
			{items.length > 0 && (
				<div
					style={{
						display: "flex",
						flexWrap: "wrap",
						gap: 4,
						marginTop: 4,
					}}
				>
					{items.map((item) => (
						<button
							type="button"
							key={item.key}
							className="badge bg-danger border-0"
							style={{
								display: "inline-flex",
								alignItems: "center",
								gap: 4,
								cursor: "pointer",
								fontSize: 11,
							}}
							onClick={() => onRemove(item.key)}
						>
							{renderItem(item)}
							<span style={{ fontWeight: "bold" }}>&times;</span>
						</button>
					))}
				</div>
			)}
		</div>
	);
}
